import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowPathIcon, DocumentDuplicateIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "../../forms/Buttons/Button";
import { copyFolderMatrix } from "../../../services/questionService";
import { copyErrorMessage, hasRows, summaryText } from "./CopyMatrixDialog";
import { Spinner, focusRing, linkButtonClass, selectClass } from "./MatrixParts";
import { gradeLabel, isSchoolGrade, levelsText, plannedOf } from "./matrixModel";

// A second way in as blue text (no frame), as tall as the buttons beside it.
const textButtonClass = `inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg px-3 text-sm font-medium text-blue-700 transition duration-200 enabled:hover:bg-blue-50 enabled:hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 max-md:w-full ${focusRing}`;

/**
 * A subject's matrix before it has rows. Readers learn the moderator has not planned it yet. Who edits it
 * (`canEdit`) begins with a grade (`onBegin(grade)`; `grades` are the ones the subject has topics for, null while
 * they load) or copies another folder's matrix of the same subject (`sources` of copySources, read into
 * `summaries`): that is offered, and recommended, only once every source is read and some have rows; otherwise a
 * line says why there is nothing to copy (`onRetry()` reads them again). In a «БТС-1»…«БТС-4» folder (`template`)
 * the way in is its week template instead (`onBeginTemplate(grade)`, a school grade): starting without it and
 * copying stay as the quiet ways. `existing`: the subject's questions so far. `onCopied(matrix, source)` gets the
 * copied matrix. `note` replaces the readers' line for one who may not begin it here (e.g. on a pre-v2 server).
 * `help`: the guide's "How it works" link, under the lead (the matrix's header has no "?" meanwhile).
 */
export default function MatrixEmpty({
  folderUuid,
  canEdit,
  existing,
  grades,
  sources,
  summaries,
  template = null,
  note = "",
  help = null,
  onBegin,
  onBeginTemplate,
  onCopied,
  onRetry,
}) {
  const [t] = useTranslation();
  const ids = useId();
  const [grade, setGrade] = useState("");
  const [picked, setPicked] = useState("");
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState("");
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (grade === "" && grades?.length) setGrade(String(grades[0]));
  }, [grades, grade]);

  const read = sources.every(({ folder: f }) => summaries[f.uuid]);
  const filled = read ? sources.filter(({ folder: f }) => hasRows(summaries[f.uuid])) : [];
  const unread = sources.some(({ folder: f }) => summaries[f.uuid]?.rows === null);
  const source = filled.some(({ folder: f }) => f.uuid === picked) ? picked : filled[0]?.folder.uuid || "";
  const weeks = template?.weeks || [];
  const useTemplate = weeks.length > 0;
  // Copying is the recommended way in, unless the folder has its template.
  const copyFirst = filled.length > 0 && !useTemplate;

  const copy = async () => {
    if (!source || copying) return;
    setCopying(true);
    setCopyError("");
    try {
      const matrix = await copyFolderMatrix(folderUuid, source);
      if (alive.current) onCopied(matrix, filled.find(({ folder: f }) => f.uuid === source));
    } catch (err) {
      if (alive.current) setCopyError(copyErrorMessage(t, err));
    } finally {
      if (alive.current) setCopying(false);
    }
  };

  // Where the template leads the way, the copy card just appears once some source has rows.
  let sourcesLine = null;
  if (sources.length && !read && !useTemplate) {
    sourcesLine = (
      <p role="status" className="flex items-center gap-2 text-sm text-slate-500">
        <Spinner className="h-4 w-4" />
        {t("folders.matrix.empty_copy_checking")}
      </p>
    );
  } else if (sources.length && !filled.length && !useTemplate) {
    sourcesLine = unread ? (
      <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
        {t("folders.matrix.empty_copy_failed")}
        <button type="button" onClick={onRetry} className={linkButtonClass}>
          <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {t("folders.matrix.retry")}
        </button>
      </p>
    ) : (
      <p className="text-sm text-slate-600">{t("folders.matrix.empty_copy_none")}</p>
    );
  }

  const gradeSelect = (
    <div>
      <label htmlFor={`${ids}-grade`} className="mb-1 block text-xs font-semibold text-slate-700">
        {t("folders.matrix.empty_new_grade")}
      </label>
      <select id={`${ids}-grade`} value={grade} onChange={(e) => setGrade(e.target.value)} className={`${selectClass} h-10 w-48 max-md:h-11 max-md:w-full`}>
        {!grades && <option value="">{t("table.loading")}</option>}
        {grades?.map((g) => (
          <option key={g} value={g}>
            {gradeLabel(t, g)}
          </option>
        ))}
      </select>
    </div>
  );

  const copyCard = filled.length > 0 && (
    <div className={`flex flex-col gap-3 rounded-xl p-5 ${copyFirst ? "border-2 border-blue-600" : "border border-slate-200"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-slate-900">{t("folders.matrix.empty_copy_title")}</h3>
        {copyFirst && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{t("folders.matrix.empty_copy_badge")}</span>}
      </div>
      <p className="text-sm text-slate-600">{t("folders.matrix.empty_copy_text")}</p>
      <div>
        <label htmlFor={`${ids}-source`} className="mb-1 block text-xs font-semibold text-slate-700">
          {t("folders.matrix.copy_source")}
        </label>
        <select
          id={`${ids}-source`}
          value={source}
          onChange={(e) => setPicked(e.target.value)}
          disabled={copying}
          className={`${selectClass} h-10 w-full max-md:h-11`}
        >
          {filled.map(({ folder: f, label }) => (
            <option key={f.uuid} value={f.uuid}>
              {`${label} — ${summaryText(t, summaries[f.uuid])}`}
            </option>
          ))}
        </select>
      </div>
      {copyError && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {copyError}
        </p>
      )}
      <Button
        className="self-start max-md:h-11 max-md:w-full"
        text={t("folders.matrix.empty_copy_button")}
        Icon={DocumentDuplicateIcon}
        color={copyFirst ? "primary" : "secondary-outline"}
        onClick={copy}
        loading={copying}
      />
    </div>
  );

  let startCard;
  if (useTemplate) {
    // The template's weeks come as rows without a topic, which a school grade only can have.
    const schoolGrade = grade !== "" && isSchoolGrade(Number(grade));
    startCard = (
      <div className="flex flex-col gap-3 rounded-xl border-2 border-blue-600 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-slate-900">{t("folders.matrix.empty_template_title", { round: template.round })}</h3>
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">{t("folders.matrix.empty_copy_badge")}</span>
        </div>
        <p className="text-sm leading-6 text-slate-600">
          {t("folders.matrix.empty_template_text", {
            weeks: t("folders.matrix.weeks_n", { count: weeks.length }),
            levels: levelsText(plannedOf(weeks)),
          })}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="max-md:w-full">{gradeSelect}</div>
          <Button
            text={t("folders.matrix.empty_template_start", { round: template.round })}
            Icon={PlusIcon}
            color="primary"
            onClick={() => onBeginTemplate(Number(grade))}
            disabled={!schoolGrade || copying}
            className="max-md:h-11 max-md:w-full"
          />
          {/* The other way in, quiet beside the main one. */}
          <button type="button" onClick={() => onBegin(Number(grade))} disabled={grade === "" || copying} className={textButtonClass}>
            {t("folders.matrix.empty_start_blank")}
          </button>
        </div>
      </div>
    );
  } else {
    startCard = (
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 p-5">
        <h3 className="text-base font-semibold text-slate-900">{t("folders.matrix.empty_new_title")}</h3>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-600">
          <li>{t("folders.matrix.empty_new_step1")}</li>
          <li>{t("folders.matrix.empty_new_step2")}</li>
          <li>{t("folders.matrix.empty_new_step3")}</li>
        </ol>
        <div className="flex flex-wrap items-end gap-3">
          {gradeSelect}
          <Button
            text={t("buttons.begin")}
            Icon={PlusIcon}
            color={filled.length ? "secondary-outline" : "primary"}
            onClick={() => onBegin(Number(grade))}
            disabled={grade === "" || copying}
            className="max-md:h-11"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 px-4 py-6 sm:px-6">
      <div>
        <p className="text-lg font-semibold text-slate-900">{t("folders.matrix.empty_title")}</p>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
          {canEdit ? t("folders.matrix.empty_lead") : note || t("folders.matrix.empty_text_view")}
        </p>
        {existing > 0 && <p className="mt-1 text-xs tabular-nums text-slate-500">{t("folders.matrix.empty_questions", { count: existing })}</p>}
        {help && <div className="-ml-1.5 mt-1">{help}</div>}
      </div>
      {canEdit && sourcesLine}
      {canEdit && (
        <div className={`grid gap-4 ${filled.length ? "lg:grid-cols-2" : "max-w-2xl"}`}>
          {copyFirst ? (
            <>
              {copyCard}
              {startCard}
            </>
          ) : (
            <>
              {startCard}
              {copyCard}
            </>
          )}
        </div>
      )}
    </div>
  );
}
